import { normalizeLocale, type SupportedLocale } from '@/i18n/config';

interface LoaderCopy {
  movingAlong: string;
  inMotion: string;
  ideasForming: string;
  shapingWorkspace: string;
  tuningSettings: string;
  openingWorkspace: string;
  startingStudio: string;
}

const EN_COPY: LoaderCopy = {
  movingAlong: 'Moving you along',
  inMotion: 'In motion',
  ideasForming: 'Ideas forming',
  shapingWorkspace: 'Gathering your workspace',
  tuningSettings: 'Tuning your settings',
  openingWorkspace: 'Opening your workspace',
  startingStudio: 'Starting Studio',
};

const COPY_BY_LOCALE = {
  en: { shapingWorkspace: EN_COPY.shapingWorkspace },
  ar: {
    movingAlong: 'جارٍ نقلك إلى الخطوة التالية',
    shapingWorkspace: 'جارٍ تجهيز مساحة العمل',
  },
  de: {
    movingAlong: 'Sie werden weitergeleitet',
    shapingWorkspace: 'Arbeitsbereich wird geladen',
  },
  es: {
    movingAlong: 'Te estamos llevando al siguiente paso',
    shapingWorkspace: 'Preparando tu espacio de trabajo',
  },
  'pt-BR': {
    movingAlong: 'Levando você para a próxima etapa',
    inMotion: 'Em andamento',
    ideasForming: 'Ideias tomando forma',
    shapingWorkspace: 'Reunindo seu espaço de trabalho',
    tuningSettings: 'Ajustando suas configurações',
    openingWorkspace: 'Abrindo seu espaço de trabalho',
    startingStudio: 'Iniciando o Studio',
  },
  fr: {
    movingAlong: 'Nous vous guidons vers la suite',
    shapingWorkspace: 'Préparation de votre espace de travail',
  },
  hi: {
    movingAlong: 'आपको आगे ले जाया जा रहा है',
    inMotion: 'प्रक्रिया जारी है',
    ideasForming: 'विचार आकार ले रहे हैं',
    shapingWorkspace: 'आपका कार्यक्षेत्र तैयार किया जा रहा है',
    tuningSettings: 'आपकी सेटिंग्स तैयार की जा रही हैं',
    openingWorkspace: 'आपका कार्यक्षेत्र खोला जा रहा है',
    startingStudio: 'स्टूडियो शुरू किया जा रहा है',
  },
  vi: {
    movingAlong: 'Đang chuyển bạn sang bước tiếp theo',
    inMotion: 'Đang xử lý',
    ideasForming: 'Ý tưởng đang thành hình',
    shapingWorkspace: 'Đang chuẩn bị không gian làm việc của bạn',
    tuningSettings: 'Đang điều chỉnh cài đặt',
    openingWorkspace: 'Đang mở không gian làm việc',
    startingStudio: 'Đang khởi động Studio',
  },
  id: {
    movingAlong: 'Membawa Anda ke langkah berikutnya',
    inMotion: 'Sedang diproses',
    ideasForming: 'Ide sedang terbentuk',
    shapingWorkspace: 'Menyiapkan workspace Anda',
    tuningSettings: 'Menyesuaikan pengaturan Anda',
    openingWorkspace: 'Membuka ruang kerja Anda',
    startingStudio: 'Memulai Studio',
  },
  it: {
    movingAlong: 'Ti stiamo portando al passaggio successivo',
    shapingWorkspace: 'Preparazione del workspace',
  },
  ja: {
    movingAlong: '次の画面へ移動しています',
    shapingWorkspace: 'ワークスペースを準備しています',
  },
  ko: {
    movingAlong: '다음 단계로 이동 중입니다',
    shapingWorkspace: '워크스페이스를 준비하는 중',
  },
  ru: {
    movingAlong: 'Переходим к следующему шагу',
    shapingWorkspace: 'Подготавливаем рабочее пространство',
  },
  zh: {
    movingAlong: '正在前往下一步',
    shapingWorkspace: '正在准备你的工作区',
  },
  'zh-TW': {
    movingAlong: '正在前往下一步',
    shapingWorkspace: '正在準備你的工作區',
  },
} satisfies Record<
  SupportedLocale,
  Partial<LoaderCopy> & Pick<LoaderCopy, 'shapingWorkspace'>
>;

export const getLoaderCopy = (locale: string): LoaderCopy => {
  return {
    ...EN_COPY,
    ...COPY_BY_LOCALE[normalizeLocale(locale)],
  };
};

const STATIC_LOADER_MESSAGE_KEYS: Readonly<Record<string, keyof LoaderCopy>> = {
  'Moving you along': 'movingAlong',
  'Moving you along...': 'movingAlong',
};

export const localizeLoaderMessage = (
  locale: string,
  message?: string
): string | undefined => {
  if (!message) return message;
  const key = STATIC_LOADER_MESSAGE_KEYS[message];
  return key ? getLoaderCopy(locale)[key] : message;
};
